/**
 * Centralized circle hierarchy utilities.
 * Single source of truth for circle categorization logic across the project.
 *
 * Backend structure (from DawaerDbContextSeed):
 * - Root circles: literature, poetry, soul, visuals, sound, philosophy, heritage, arts, sufsim, language, journey
 * - Poetry (دائرة الشعور) has children: classical-poetry, modern-poetry, colloquial-poetry
 *
 * Hierarchy (current):
 * - دائرة الأدب (literature) - root
 * - دائرة الشعور (poetry) - root, has 3 subcategories
 *   - شعرٌ فصيح (classical-poetry)
 *   - شعرٌ حديث (modern-poetry)
 *   - شعرٌ عامّي (colloquial-poetry)
 */

/**
 * Get root circles only (no parent) - for filters, signup selection, etc.
 * Excludes poetry subcategories (شعرٌ فصيح، شعرٌ حديث، شعرٌ عامّي).
 * @param {Array} circles - Full circles array from API
 * @returns {Array} Root circles
 */
export const getRootCircles = (circles = []) => circles.filter((c) => !c.parentId);

/**
 * Get circles that should appear in filter dropdowns.
 * Same as root circles - only top-level categories.
 * @param {Array} circles - Full circles array from API
 * @returns {Array} Filterable circles
 */
export const getFilterableCircles = (circles = []) => getRootCircles(circles);

/**
 * Get all slugs for a circle including its descendants.
 * When filtering by "poetry", include articles in classical-poetry, modern-poetry, colloquial-poetry.
 * @param {Array} circles - Full circles array from API
 * @param {string} slug - Circle slug
 * @returns {string[]} Slugs to match (circle + descendants)
 */
export const getSlugsForCircle = (circles = [], slug) => {
  const circle = circles.find((c) => c.slug === slug);
  if (!circle) return [slug];
  const descendantSlugs = circles.filter((c) => c.parentId === circle.id).map((c) => c.slug);
  return [slug, ...descendantSlugs];
};

/**
 * Get children of a circle (e.g. poetry subcategories).
 * @param {Array} circles - Full circles array from API
 * @param {string} parentSlugOrId - Parent slug or GUID
 * @returns {Array} Child circles
 */
export const getChildCircles = (circles = [], parentSlugOrId) => {
  const parent = circles.find((c) => c.slug === parentSlugOrId || c.id === parentSlugOrId);
  if (!parent) return [];
  return circles.filter((c) => c.parentId === parent.id);
};
